import { createSelector } from '@reduxjs/toolkit'

import type { RootState } from '@/store/root-reducer'

import { normalizeEmail } from './normalize'
import { accountsAdapter, relationshipsAdapter, usersAdapter } from './store'
import { usersScopeOf } from './permissions'
import { fullName, type Account, type CurrentUser, type User } from './types'

const accountSelectors = accountsAdapter.getSelectors((state: RootState) => state.directory.accounts)
const userSelectors = usersAdapter.getSelectors((state: RootState) => state.directory.users)
const relationshipSelectors = relationshipsAdapter.getSelectors((state: RootState) => state.directory.relationships)

export const selectAllAccounts = accountSelectors.selectAll
export const selectAllUsers = userSelectors.selectAll
export const selectAllRelationships = relationshipSelectors.selectAll
export const selectAccountById = accountSelectors.selectById
export const selectUserById = userSelectors.selectById

const selectSession = (state: RootState) => state.auth.session

/** The signed-in user (this tab's session) joined with their account, or null. Stable until something it reads changes. */
export const selectCurrentUser = createSelector(
  [selectSession, (state: RootState) => state.directory.users.entities, (state: RootState) => state.directory.accounts.entities],
  (session, users, accounts): CurrentUser | null => {
    const user = session ? users[session.userId] : undefined
    const account = user ? accounts[user.accountId] : undefined
    return user && account ? { ...user, fullName: fullName(user), account } : null
  },
)

export function findUserByEmail(state: RootState, email: string): User | undefined {
  const wanted = normalizeEmail(email)
  return selectAllUsers(state).find((user) => normalizeEmail(user.email) === wanted)
}

/** The supplier a client belongs to. A client has exactly one. */
export function findSupplierOfClient(state: RootState, clientAccountId: string): Account | undefined {
  const link = selectAllRelationships(state).find(
    (item) => item.relationshipType === 'SUPPLIER_CLIENT' && item.childAccountId === clientAccountId,
  )
  return link ? selectAccountById(state, link.parentAccountId) : undefined
}

export function findClientIdsOfSupplier(state: RootState, supplierAccountId: string): string[] {
  return selectAllRelationships(state)
    .filter((item) => item.relationshipType === 'SUPPLIER_CLIENT' && item.parentAccountId === supplierAccountId)
    .map((item) => item.childAccountId)
}

export interface UserRow {
  user: User
  fullName: string
  account: Account
}

/**
 * The people the signed-in user may see on the Users screen: everyone for a SupplyLogic admin, the same account for a
 * supplier or client admin, nobody for everyone else. Newest first.
 */
export const selectVisibleUsers = createSelector(
  [selectAllUsers, (state: RootState) => state.directory.accounts.entities, selectCurrentUser],
  (users, accounts, me): UserRow[] => {
    if (!me) return []
    const scope = usersScopeOf(me)
    if (scope === 'NONE') return []
    return users
      .filter((user) => scope === 'ALL' || user.accountId === me.accountId)
      .flatMap((user) => {
        const account = accounts[user.accountId]
        return account ? [{ user, fullName: fullName(user), account }] : []
      })
      .toSorted((a, b) => b.user.createdAt.localeCompare(a.user.createdAt) || a.fullName.localeCompare(b.fullName))
  },
)
